import React, { useState, useEffect, useMemo, useCallback } from 'react';
import WalkthroughButton from '../WalkthroughButton';
import PortfolioParamsForm from './PortfolioParamsForm';
import PortfolioPanel from './PortfolioPanel';
import WatchlistTable from '../WatchlistTable';
import LoadingOverlay from '../LoadingOverlay';
import ErrorBoundary from '../ErrorBoundary';
import CollapsibleSection from '../primitives/CollapsibleSection';
import FeatureGate from '../FeatureGate';
import PortfolioIntel from './Intel/PortfolioIntel';
import IntelUpgradeNotice from './Intel/IntelUpgradeNotice';
import apiClient, { getPortfolio, simulateGroupContracts } from '../../services/api';
import {
  isUsageLimitError,
  getUsageLimitData,
  isFeatureAccessError,
  getFeatureAccessData,
} from '../../utils/usageLimitHelper';
import UsageLimitModal from '../UsageLimitModal';
import FeatureAccessModal from '../FeatureAccessModal';
import { BASELINE_MODES, DEFAULT_BASELINE_MODE } from '../../utils/watchlistBaseline';
import {
  PORTFOLIO_CACHE_KEY,
  PORTFOLIO_CACHE_TIMESTAMP_KEY,
  PORTFOLIO_CACHE_TTL_MINUTES,
  PORTFOLIO_BASELINE_MODE_KEY,
  PORTFOLIO_COLLAPSE_KEYS,
  splitByAssetType,
} from './portfolioConstants';

/**
 * Portfolio page — what you actually own.
 *
 * Structurally this is the Watchlist page narrowed to a single group: the same
 * entry form, holdings cards, simulation table, elasticity heatmap and filters,
 * but with one fixed destination instead of many groups, plus plain stock
 * alongside option contracts.
 *
 * The results table and heatmap are the watchlist's components, used verbatim.
 * Only option positions reach them — stock is tracking-only and lives in the
 * holdings cards and the summary above.
 */
export default function Portfolio() {
  const [portfolio, setPortfolio] = useState(null);
  const [results, setResults] = useState([]);
  const [selectedContracts, setSelectedContracts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Table filters (lifted so they survive view toggles)
  const [selectedTickers, setSelectedTickers] = useState([]);
  const [selectedExpirations, setSelectedExpirations] = useState([]);
  const [selectedScenarios, setSelectedScenarios] = useState([]);

  // Simulation view state
  const [simulationMode, setSimulationMode] = useState('custom');
  const [customDaysToTarget, setCustomDaysToTarget] = useState(null);
  const [customUnderlyingGain, setCustomUnderlyingGain] = useState(null);
  const [daysToExitMode, setDaysToExitMode] = useState('fixed');
  const [daysToExitPercent, setDaysToExitPercent] = useState(null);

  const [isSimulating, setIsSimulating] = useState(false);
  const [simulationProgress, setSimulationProgress] = useState({ current: 0, total: 0 });
  // Bumped when results arrive, so a collapsed Simulation section opens itself
  // rather than silently swallowing the run the user just triggered.
  const [simExpandSignal, setSimExpandSignal] = useState(0);
  const [usageLimitData, setUsageLimitData] = useState(null);
  const [featureAccessData, setFeatureAccessData] = useState(null);

  // Portfolio keeps its own baseline preference so toggling it here doesn't
  // silently change what the Watchlist page is comparing against.
  const [baselineMode, setBaselineMode] = useState(() => {
    try {
      const saved = localStorage.getItem(PORTFOLIO_BASELINE_MODE_KEY);
      if (saved === BASELINE_MODES.AVG_COST || saved === BASELINE_MODES.CURRENT) return saved;
    } catch {
      /* noop */
    }
    return DEFAULT_BASELINE_MODE;
  });

  useEffect(() => {
    try {
      localStorage.setItem(PORTFOLIO_BASELINE_MODE_KEY, baselineMode);
    } catch {
      /* noop */
    }
  }, [baselineMode]);

  // --- Load the portfolio group (created server-side on first access) ---
  const fetchPortfolio = useCallback(async () => {
    try {
      const response = await getPortfolio();
      setPortfolio(response.data);
      return response.data;
    } catch (error) {
      console.error('Error loading portfolio:', error.response?.data || error.message);
      if (isFeatureAccessError(error)) {
        setFeatureAccessData(getFeatureAccessData(error));
      }
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPortfolio();

    // Restore cached simulation results so the table isn't empty on return.
    try {
      const cached = localStorage.getItem(PORTFOLIO_CACHE_KEY);
      const cachedAt = localStorage.getItem(PORTFOLIO_CACHE_TIMESTAMP_KEY);
      if (cached && cachedAt) {
        const minutesOld = (Date.now() - parseInt(cachedAt, 10)) / (1000 * 60);
        if (minutesOld < PORTFOLIO_CACHE_TTL_MINUTES) {
          setResults(JSON.parse(cached));
        } else {
          localStorage.removeItem(PORTFOLIO_CACHE_KEY);
          localStorage.removeItem(PORTFOLIO_CACHE_TIMESTAMP_KEY);
        }
      }
    } catch (err) {
      console.error('Error restoring portfolio cache:', err);
    }
  }, [fetchPortfolio]);

  const cacheResults = (data) => {
    try {
      localStorage.setItem(PORTFOLIO_CACHE_KEY, JSON.stringify(data));
      localStorage.setItem(PORTFOLIO_CACHE_TIMESTAMP_KEY, Date.now().toString());
    } catch (err) {
      console.error('Error caching portfolio results:', err);
    }
  };

  // --- Simulation lifecycle ---
  const handleSimulationStart = (count) => {
    setIsSimulating(true);
    setSimulationProgress({ current: 0, total: count });

    let current = 0;
    const interval = setInterval(() => {
      if (current < count) {
        current += 1;
        setSimulationProgress((prev) => ({ ...prev, current }));
      } else {
        clearInterval(interval);
      }
    }, 1000);
    window.portfolioProgressInterval = interval;
  };

  const handleSimulationComplete = (data) => {
    if (window.portfolioProgressInterval) {
      clearInterval(window.portfolioProgressInterval);
      window.portfolioProgressInterval = null;
    }
    setResults(data);
    setIsSimulating(false);
    setSimulationProgress({ current: 0, total: 0 });

    // Open the section before scrolling — otherwise a user who had it collapsed
    // gets scrolled to a closed header and the run looks like it did nothing.
    setSimExpandSignal((n) => n + 1);

    setTimeout(() => {
      document
        .querySelector('[data-portfolio-table]')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleSimulationError = (error, fallbackMessage) => {
    console.error(fallbackMessage, error.response?.data || error.message);
    setIsSimulating(false);
    if (isUsageLimitError(error)) {
      setUsageLimitData(getUsageLimitData(error));
    } else if (isFeatureAccessError(error)) {
      setFeatureAccessData(getFeatureAccessData(error));
    } else {
      alert(`${fallbackMessage} ${error.message}`);
    }
  };

  // Only option positions are simulated; stock has nothing to project.
  const optionContracts = useMemo(
    () => splitByAssetType(portfolio?.contracts).options,
    [portfolio]
  );
  const stockContracts = useMemo(
    () => splitByAssetType(portfolio?.contracts).stocks,
    [portfolio]
  );

  const handleRunPortfolio = async (portfolioId) => {
    if (!portfolioId) return;
    try {
      handleSimulationStart(optionContracts.length);
      const response = await simulateGroupContracts(portfolioId);
      if (Array.isArray(response.data)) {
        handleSimulationComplete(response.data);
        cacheResults(response.data);
        // The simulate endpoint also writes refreshed values back to the DB,
        // so pull the holdings again to keep the cards and totals in step.
        await fetchPortfolio();
      } else {
        setIsSimulating(false);
        alert(`Unexpected server response: ${JSON.stringify(response.data)}`);
      }
    } catch (error) {
      handleSimulationError(error, 'Failed to run portfolio:');
    }
  };

  /**
   * Run only the checked positions.
   *
   * Uses /run-watchlist/ with full contract payloads rather than
   * /run-bulk-watchlist/ with ids: the bulk endpoint zips its 7-rows-per-contract
   * result against a 1-per-contract input list, which truncates the results and
   * pairs rows with the wrong cost basis.
   */
  const handleRunSelected = async () => {
    const selected = optionContracts.filter((c) => selectedContracts.includes(c.id));
    if (selected.length === 0) {
      handleSimulationComplete([]);
      alert('Select at least one option position to simulate. Stock holdings are not simulated.');
      return;
    }

    try {
      const payload = selected.map((c) => ({
        ticker: c.ticker,
        option_type: c.option_type,
        position_type: c.position_type || 'buy',
        strike: parseFloat(c.strike),
        expiration: c.expiration,
        days_to_gain: c.dynamic_days_to_gain,
        number_of_contracts: c.number_of_contracts,
        average_cost_per_contract: c.average_cost_per_contract,
      }));

      const response = await apiClient.post('/run-watchlist/', { contracts: payload });
      if (Array.isArray(response.data)) {
        handleSimulationComplete(response.data);
        cacheResults(response.data);
      } else {
        handleSimulationComplete([]);
      }
    } catch (error) {
      handleSimulationComplete([]);
      handleSimulationError(error, 'Failed to run selected positions:');
    }
  };

  // Guard the table against stock rows leaking into the Black-Scholes views.
  const optionResults = useMemo(() => splitByAssetType(results).options, [results]);

  return (
    <div className="p-4 text-text portfolio-theme">
      <div className="flex items-center gap-3 mb-2">
        <h2 className="text-2xl font-bold text-brand-white">Portfolio</h2>
        <WalkthroughButton sectionKey="portfolio" />
      </div>
      <p className="text-sm text-muted mb-6">
        Everything you actually own, on one page. To track contracts you don't hold, use
        Watchlist groups.
      </p>

      {isLoading ? (
        <p className="text-muted">Loading your portfolio...</p>
      ) : (
        <>
          <CollapsibleSection
            title="Add Positions"
            subtitle="Add an option contract or a stock holding to your portfolio."
            storageKey={PORTFOLIO_COLLAPSE_KEYS.ADD}
            // Open on an empty portfolio — otherwise a new user lands on a
            // collapsed form above an empty list with no obvious next step.
            defaultExpanded={(portfolio?.contracts?.length || 0) === 0}
          >
            <PortfolioParamsForm portfolioId={portfolio?.id} onSaved={fetchPortfolio} />
          </CollapsibleSection>

          <CollapsibleSection
            title="Your Portfolio"
            summary={`${portfolio?.contracts?.length || 0} position${
              (portfolio?.contracts?.length || 0) === 1 ? '' : 's'
            }`}
            storageKey={PORTFOLIO_COLLAPSE_KEYS.HOLDINGS}
            defaultExpanded
          >
            <PortfolioPanel
              portfolio={portfolio}
              onRunPortfolio={handleRunPortfolio}
              refetchPortfolio={fetchPortfolio}
              selectedContracts={selectedContracts}
              setSelectedContracts={setSelectedContracts}
              onRunSelected={handleRunSelected}
              onSimulationStart={handleSimulationStart}
              onSimulationComplete={handleSimulationComplete}
              baselineMode={baselineMode}
              setBaselineMode={setBaselineMode}
            />
          </CollapsibleSection>

          {/* The data-portfolio-table attribute stays on an always-rendered
              wrapper so the post-simulation auto-scroll still finds a target
              even while the section is collapsed. */}
          <div data-portfolio-table>
            <CollapsibleSection
              title="Simulation & Elasticity Heatmap"
              subtitle="Scenario table, heatmap, simulation settings and filters."
              storageKey={PORTFOLIO_COLLAPSE_KEYS.SIMULATION}
              defaultExpanded
              expandSignal={simExpandSignal}
            >
              {stockContracts.length > 0 && optionResults.length > 0 && (
                <p className="text-xs text-muted mb-2 italic">
                  {stockContracts.length} stock holding
                  {stockContracts.length === 1 ? '' : 's'} not shown below — stock tracks its
                  price 1:1, so there is nothing to simulate. See the cards above.
                </p>
              )}
              <ErrorBoundary label="The portfolio table">
              <WatchlistTable
                items={optionResults}
                filterAccent="#218aff"
                filterAccentText="#ffffff"
                filterSurface="#1a1a1a"
                selectedTickers={selectedTickers}
                setSelectedTickers={setSelectedTickers}
                selectedExpirations={selectedExpirations}
                setSelectedExpirations={setSelectedExpirations}
                selectedScenarios={selectedScenarios}
                setSelectedScenarios={setSelectedScenarios}
                simulationMode={simulationMode}
                onSimulationModeChange={setSimulationMode}
                customDaysToTarget={customDaysToTarget}
                customUnderlyingGain={customUnderlyingGain}
                onCustomSettingsChange={(days, gain) => {
                  setCustomDaysToTarget(days);
                  setCustomUnderlyingGain(gain);
                }}
                daysToExitMode={daysToExitMode}
                setDaysToExitMode={setDaysToExitMode}
                daysToExitPercent={daysToExitPercent}
                setDaysToExitPercent={setDaysToExitPercent}
                setCustomDaysToTarget={setCustomDaysToTarget}
                baselineMode={baselineMode}
                setBaselineMode={setBaselineMode}
              />
              </ErrorBoundary>
            </CollapsibleSection>
          </div>

          <CollapsibleSection
            title="Company Intel"
            subtitle="Fundamentals, financials, estimates and analyst targets for the companies you hold."
            storageKey={PORTFOLIO_COLLAPSE_KEYS.INTEL}
            defaultExpanded={false}
            dataTour="portfolio-intel"
          >
            <ErrorBoundary label="Company Intel">
              <FeatureGate feature="stock_analyzer" fallback={<IntelUpgradeNotice />}>
                <PortfolioIntel contracts={portfolio?.contracts || []} />
              </FeatureGate>
            </ErrorBoundary>
          </CollapsibleSection>
        </>
      )}

      <LoadingOverlay
        isVisible={isSimulating}
        currentProgress={simulationProgress.current}
        totalProgress={simulationProgress.total}
        message="Running Portfolio Simulation"
      />

      {usageLimitData && (
        <UsageLimitModal data={usageLimitData} onClose={() => setUsageLimitData(null)} />
      )}
      {featureAccessData && (
        <FeatureAccessModal data={featureAccessData} onClose={() => setFeatureAccessData(null)} />
      )}
    </div>
  );
}
